// Projected-revenue report builder for draft schedules.
//
// Turns the current draft sessions into a "revenue by week" grid:
//   - Courses (not sessions) down the side, grouped by location
//   - Weeks across the top, grouped by month
//   - Each cell = projected revenue for that course, in that week, at that
//     location = projected seats taken x per-seat course cost
//
// This module is pure (no XLSX / DOM / network) so it can be unit tested. The
// component turns the returned structure into an .xlsx workbook.

import { getCourseCost as manualCourseCost } from './courseCosts';
import { classifyHub, hubName, HUB_CODES } from './hubLocation';

// Normalise a course name for case/whitespace-insensitive matching against the
// mi_course catalog. Must match the normalisation used when the price map is
// built (see scheduleGeneratorService.getCoursePrices).
export function normalizeCourseName(name) {
  return String(name || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Build a cost resolver backed by Dataverse course prices, falling back to the
 * manual override map in courseCosts.js. The resolver matches by course GUID
 * first (most reliable), then by normalised course name.
 *
 * @param {{ byGuid?: Record<string, number>, byName?: Record<string, number> }} prices
 * @param {(name:string)=>number|null} [manualLookup] override map lookup
 * @returns {(courseName:string, session?:object)=>number|null}
 */
export function createPriceResolver(prices = {}, manualLookup = manualCourseCost) {
  const byGuid = prices.byGuid || {};
  const byName = prices.byName || {};
  return (courseName, session) => {
    const guid = session?.courseGuid;
    if (guid && Object.prototype.hasOwnProperty.call(byGuid, guid)) return byGuid[guid];
    const key = normalizeCourseName(courseName);
    if (Object.prototype.hasOwnProperty.call(byName, key)) return byName[key];
    return manualLookup ? manualLookup(courseName) : null;
  };
}

// Default resolver: Dataverse prices unavailable -> manual override map only.
const defaultGetCourseCost = (courseName) => manualCourseCost(courseName);

// How "projected seats taken" is derived from a draft session.
//   'fillRate' -> maxCapacity x historical average fill rate (realistic)
//   'capacity' -> maxCapacity (every seat sold)
// Change this one constant to switch the whole report.
export const SEAT_PROJECTION_MODE = 'fillRate';

/**
 * Projected seats taken for a single draft session.
 * @param {object} session draft session ({ maxCapacity, avgFillRate })
 * @param {string} [mode] SEAT_PROJECTION_MODE override
 * @returns {number} whole number of seats
 */
export function projectedSeats(session, mode = SEAT_PROJECTION_MODE) {
  const capacity = Number(session?.maxCapacity) || 0;
  if (mode === 'capacity') return capacity;
  const fill = Number(session?.avgFillRate);
  // No usable fill-rate history -> fall back to full capacity so the course is
  // never silently valued at zero. Flagged on the Assumptions sheet.
  if (!Number.isFinite(fill) || fill <= 0) return capacity;
  return Math.round(capacity * fill);
}

// Parse the date portion of an ISO string as a LOCAL date (avoids the UTC
// day-shift bug that would move a Monday session into the previous week).
function parseLocalDate(iso) {
  if (!iso) return null;
  const parts = String(iso).slice(0, 10).split('-');
  if (parts.length !== 3) return null;
  const [y, m, d] = parts.map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

// Monday (00:00 local) of the week containing `date`.
function getMonday(date) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = d.getDay(); // 0=Sun .. 6=Sat
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

function toKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function columnName(index) {
  let name = '';
  for (let n = index + 1; n > 0; n = Math.floor((n - 1) / 26)) {
    name = String.fromCharCode(65 + ((n - 1) % 26)) + name;
  }
  return name;
}

function formulaCell(formula, value) {
  return { t: 'n', f: formula, v: value };
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function weekLabel(monday) {
  const d = String(monday.getDate()).padStart(2, '0');
  return `w/c ${d} ${MONTHS[monday.getMonth()].slice(0, 3)}`;
}

/**
 * Build the projected-revenue-by-week report for a set of draft sessions.
 *
 * @param {Array<object>} sessions draft sessions
 * @param {object} [opts]
 * @param {(name:string)=>number|null} [opts.getCourseCost] price-per-seat lookup
 * @param {number} [opts.year] include this calendar year only and show its full week timeline
 * @param {string} [opts.seatMode] SEAT_PROJECTION_MODE override
 * @param {boolean} [opts.includeBundleChildren] count bundle child sessions too
 *        (default false: only the sellable parent/standalone course earns)
 * @returns {object} report structure consumed by revenueReportToWorkbookData
 */
export function buildRevenueReport(sessions, opts = {}) {
  const {
    getCourseCost = defaultGetCourseCost,
    year = null,
    seatMode = SEAT_PROJECTION_MODE,
    includeBundleChildren = false
  } = opts;

  const list = Array.isArray(sessions) ? sessions : [];

  // 1. Filter to revenue-earning sessions with a usable start date.
  const earning = list.filter(s => {
    if (!includeBundleChildren && s?.isBundleChild) return false;
    const date = parseLocalDate(s?.startDate);
    if (!date) return false;
    if (year && date.getFullYear() !== Number(year)) return false;
    // A week belongs to the year in which its Monday falls. This keeps a
    // next-year-only report from adding a prior-December week.
    if (year && getMonday(date).getFullYear() !== Number(year)) return false;
    return true;
  });

  // 2. Establish the continuous week timeline (first -> last session week).
  const mondayTimes = earning
    .map(s => getMonday(parseLocalDate(s.startDate)).getTime())
    .sort((a, b) => a - b);

  const weeks = [];       // [{ key, label, monthKey, monthLabel }]
  const weekIndex = {};   // key -> position in weeks[]
  if (year || mondayTimes.length > 0) {
    const reportYear = Number(year);
    const first = year ? new Date(reportYear, 0, 1) : new Date(mondayTimes[0]);
    if (year) first.setDate(first.getDate() + ((8 - first.getDay()) % 7));
    const last = year ? getMonday(new Date(reportYear, 11, 31)) : new Date(mondayTimes[mondayTimes.length - 1]);
    const cursor = new Date(first);
    while (cursor.getTime() <= last.getTime()) {
      const key = toKey(cursor);
      weekIndex[key] = weeks.length;
      weeks.push({
        key,
        label: weekLabel(cursor),
        monthKey: `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`,
        monthLabel: `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`
      });
      cursor.setDate(cursor.getDate() + 7);
    }
  }

  // 3. Month groupings (consecutive weeks sharing a monthKey) for header merges.
  const months = [];
  weeks.forEach(w => {
    const tail = months[months.length - 1];
    if (tail && tail.monthKey === w.monthKey) {
      tail.weekCount += 1;
    } else {
      months.push({ monthKey: w.monthKey, label: w.monthLabel, weekCount: 1 });
    }
  });

  // 4. Aggregate into one row per course + campus + location, by week.
  //    (Flat table — the CEO sorts/filters in Excel; no location grouping.)
  //    cells        = projected revenue ($)
  //    plannedCells = planned registrations this period (projected seats)
  //    Prior-year comparison lives on the separate "Sanity Check (by Hub)"
  //    sheet (buildHubSanityReport), not here.
  const rowsMap = new Map(); // `course|campus|location` -> row
  const missingCostSet = new Set();

  earning.forEach(s => {
    const courseName = (s.name || s.courseName || 'Untitled').trim() || 'Untitled';
    const campus = (s.campus || s.campusName || '').trim();
    const location = (s.locationName || '').trim();

    const cost = getCourseCost(courseName, s);
    if (cost === null || cost === undefined) missingCostSet.add(courseName);
    const seats = projectedSeats(s, seatMode);
    const revenue = seats * (Number(cost) || 0);

    const monday = getMonday(parseLocalDate(s.startDate));
    const weekKey = toKey(monday);

    const rowKey = `${courseName}||${campus}||${location}`;
    if (!rowsMap.has(rowKey)) {
      rowsMap.set(rowKey, {
        course: courseName,
        campus,
        location,
        courseGuid: s.courseGuid || null,
        courseCost: cost === null || cost === undefined ? null : Number(cost) || 0,
        cells: {},         // revenue ($) by week
        plannedCells: {},  // planned registrations by week
        total: 0,          // total revenue
        plannedTotal: 0    // total planned registrations
      });
    }
    const row = rowsMap.get(rowKey);
    if (!row.courseGuid && s.courseGuid) row.courseGuid = s.courseGuid;
    if (row.courseCost === null && cost !== null && cost !== undefined) row.courseCost = Number(cost) || 0;
    row.cells[weekKey] = (row.cells[weekKey] || 0) + revenue;
    row.plannedCells[weekKey] = (row.plannedCells[weekKey] || 0) + seats;
    row.total += revenue;
    row.plannedTotal += seats;
  });

  // 5. Materialise sorted rows + weekly + grand totals.
  const weeklyTotals = {};   // revenue
  const weeklyPlanned = {};  // planned registrations
  weeks.forEach(w => { weeklyTotals[w.key] = 0; weeklyPlanned[w.key] = 0; });
  let grandTotal = 0;
  let grandPlanned = 0;

  const rows = [...rowsMap.values()]
    .sort((a, b) =>
      a.course.localeCompare(b.course) ||
      a.campus.localeCompare(b.campus) ||
      a.location.localeCompare(b.location)
    );

  rows.forEach(row => {
    Object.entries(row.cells).forEach(([k, v]) => { weeklyTotals[k] += v; });
    Object.entries(row.plannedCells).forEach(([k, v]) => { weeklyPlanned[k] += v; });
    grandTotal += row.total;
    grandPlanned += row.plannedTotal;
  });

  return {
    weeks,
    months,
    rows,
    weeklyTotals,
    weeklyPlanned,
    grandTotal,
    grandPlanned,
    missingCosts: [...missingCostSet].sort((a, b) => a.localeCompare(b)),
    seatMode
  };
}

/**
 * Convert a report into the array-of-arrays + merge ranges an .xlsx sheet
 * needs. Numeric cells hold real numbers (zeros collapse to '' in the body so
 * the grid stays readable); totals stay numeric.
 *
 * @param {object} report result of buildRevenueReport
 * @returns {{ aoa: any[][], merges: object[], colWidths: {wch:number}[] }}
 */
export function revenueReportToWorkbookData(report) {
  const {
    weeks, months, rows,
    weeklyTotals, weeklyPlanned,
    grandTotal, grandPlanned
  } = report;

  // Each week spans TWO grouped columns:
  //   +0 Revenue ($)   +1 Planned regs (current period)
  // Prior-year actuals live on the separate monthly "Sanity Check (by Hub)"
  // sheet, where the coarser bucket avoids week-alignment noise.
  const COLS_PER_WEEK = 2;
  const courseCostCol = 3;
  const firstWeekCol = 4;                 // cols 0..3 = Course, Campus, Location, Course Cost
  const weekStart = i => firstWeekCol + i * COLS_PER_WEEK;
  const totalStart = weekStart(weeks.length); // totals group at the right
  const width = totalStart + COLS_PER_WEEK;

  const blankRow = () => new Array(width).fill('');
  const aoa = [];
  const merges = [];

  // Row 0: month header (merged across each month's week columns × 2).
  const monthRow = blankRow();
  let c = firstWeekCol;
  months.forEach(m => {
    const span = m.weekCount * COLS_PER_WEEK;
    monthRow[c] = m.label;
    merges.push({ s: { r: 0, c }, e: { r: 0, c: c + span - 1 } });
    c += span;
  });
  monthRow[totalStart] = 'Totals';
  merges.push({ s: { r: 0, c: totalStart }, e: { r: 0, c: totalStart + COLS_PER_WEEK - 1 } });
  aoa.push(monthRow);

  // Row 1: week header (each merged across its 2 grouped columns).
  const weekRow = blankRow();
  weeks.forEach((w, i) => {
    const ws = weekStart(i);
    weekRow[ws] = w.label;
    merges.push({ s: { r: 1, c: ws }, e: { r: 1, c: ws + COLS_PER_WEEK - 1 } });
  });
  aoa.push(weekRow);

  // Row 2: metric sub-header (repeats per week + the totals group).
  const headerRow = blankRow();
  headerRow[0] = 'Course';
  headerRow[1] = 'Campus';
  headerRow[2] = 'Location';
  headerRow[courseCostCol] = 'Course Cost';
  weeks.forEach((w, i) => {
    const ws = weekStart(i);
    headerRow[ws] = 'Revenue ($)';
    headerRow[ws + 1] = 'Planned Reg';
  });
  headerRow[totalStart] = 'Total Revenue ($)';
  headerRow[totalStart + 1] = 'Total Planned Reg';
  // Course/Campus/Location labels live on this (autofilter) row; the two rows
  // above them stay blank so filter dropdowns line up with their headers.
  aoa.push(headerRow);

  const cellVal = v => (v ? v : ''); // collapse 0 to blank in the body

  // One flat row per course + campus + location.
  rows.forEach(r => {
    const row = blankRow();
    const excelRow = aoa.length + 1;
    row[0] = r.course;
    row[1] = r.campus;
    row[2] = r.location;
    row[courseCostCol] = r.courseCost === null ? '' : r.courseCost;
    weeks.forEach((w, i) => {
      const ws = weekStart(i);
      const planned = r.plannedCells[w.key] || 0;
      row[ws + 1] = cellVal(planned);
      if (planned) {
        row[ws] = formulaCell(`$${columnName(courseCostCol)}${excelRow}*${columnName(ws + 1)}${excelRow}`, r.cells[w.key] || 0);
      }
    });
    const revenueCells = weeks.map((w, i) => `${columnName(weekStart(i))}${excelRow}`).join(',');
    const plannedCells = weeks.map((w, i) => `${columnName(weekStart(i) + 1)}${excelRow}`).join(',');
    row[totalStart] = formulaCell(`SUM(${revenueCells})`, r.total);
    row[totalStart + 1] = formulaCell(`SUM(${plannedCells})`, r.plannedTotal);
    aoa.push(row);
  });

  // Grand total row.
  const grand = blankRow();
  const firstDataExcelRow = 4;
  const lastDataExcelRow = 3 + rows.length;
  grand[0] = 'WEEKLY TOTAL';
  weeks.forEach((w, i) => {
    const ws = weekStart(i);
    if (rows.length) {
      grand[ws] = formulaCell(`SUM(${columnName(ws)}${firstDataExcelRow}:${columnName(ws)}${lastDataExcelRow})`, weeklyTotals[w.key] || 0);
      grand[ws + 1] = formulaCell(`SUM(${columnName(ws + 1)}${firstDataExcelRow}:${columnName(ws + 1)}${lastDataExcelRow})`, weeklyPlanned[w.key] || 0);
    }
  });
  if (rows.length) {
    grand[totalStart] = formulaCell(`SUM(${columnName(totalStart)}${firstDataExcelRow}:${columnName(totalStart)}${lastDataExcelRow})`, grandTotal);
    grand[totalStart + 1] = formulaCell(`SUM(${columnName(totalStart + 1)}${firstDataExcelRow}:${columnName(totalStart + 1)}${lastDataExcelRow})`, grandPlanned);
  }
  aoa.push(grand);

  const colWidths = new Array(width).fill(null).map((_, i) => {
    if (i === 0) return { wch: 34 }; // Course
    if (i === 1) return { wch: 18 }; // Campus
    if (i === 2) return { wch: 22 }; // Location
    if (i === courseCostCol) return { wch: 14 }; // Course Cost
    if (i >= totalStart) return { wch: 15 }; // totals group
    // week group: revenue a touch wider than the planned-reg count column
    return { wch: (i - firstWeekCol) % COLS_PER_WEEK === 0 ? 12 : 11 };
  });

  // Columns holding currency ($) figures: each week's revenue col + the total
  // revenue col. Everything else numeric is a registration count. The component
  // uses this to apply the right number format.
  const currencyCols = [];
  currencyCols.push(courseCostCol);
  weeks.forEach((w, i) => currencyCols.push(weekStart(i)));
  currencyCols.push(totalStart);

  // Excel autofilter over the metric sub-header (row index 2) through the last
  // data row, so the CEO gets sort/filter dropdowns on every column directly.
  const autofilterRef = { s: { r: 2, c: 0 }, e: { r: 2 + rows.length, c: width - 1 } };

  return { aoa, merges, colWidths, autofilterRef, currencyCols };
}

/**
 * Build the monthly, by-hub sanity-check comparison: for each course at each of
 * the four hubs (WA/HI/CA/VA), how many registrations are PLANNED this year vs
 * how many ACTUALLY registered in the same month last year. Monthly buckets
 * absorb week-level schedule moves; the hub roll-up absorbs city/room moves.
 *
 * @param {Array<object>} sessions draft sessions (this year's plan)
 * @param {object} [opts]
 * @param {string} [opts.seatMode] SEAT_PROJECTION_MODE override
 * @param {boolean} [opts.includeBundleChildren]
 * @param {Array<object>} [opts.priorSessions] real historical sessions (with seatsTaken)
 * @param {number} [opts.priorYearOffset] years back for 'prior period' (default 1)
 * @returns {object} structure consumed by hubSanityToWorkbookData
 */
export function buildHubSanityReport(sessions, opts = {}) {
  const {
    seatMode = SEAT_PROJECTION_MODE,
    includeBundleChildren = false,
    priorSessions = [],
    priorYearOffset = 1
  } = opts;

  const list = Array.isArray(sessions) ? sessions : [];
  const earning = list.filter(s => {
    if (!includeBundleChildren && s?.isBundleChild) return false;
    return !!parseLocalDate(s?.startDate);
  });

  const monthKeyOf = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  const monthLabelOf = key => {
    const [y, m] = key.split('-').map(Number);
    return `${MONTHS[m - 1].slice(0, 3)} ${y}`;
  };

  // 1. Continuous month timeline across the draft (first -> last draft month).
  const monthOrdinals = earning
    .map(s => { const d = parseLocalDate(s.startDate); return d.getFullYear() * 12 + d.getMonth(); })
    .sort((a, b) => a - b);
  const months = []; // [{ key, label }]
  if (monthOrdinals.length > 0) {
    for (let mm = monthOrdinals[0]; mm <= monthOrdinals[monthOrdinals.length - 1]; mm++) {
      const y = Math.floor(mm / 12);
      const m = (mm % 12) + 1;
      const key = `${y}-${String(m).padStart(2, '0')}`;
      months.push({ key, label: monthLabelOf(key) });
    }
  }

  const unclassified = new Set();
  const hubOf = (loc, camp) => {
    const code = classifyHub(loc, camp);
    if (!code) {
      const raw = String(loc || camp || '').trim();
      if (raw) unclassified.add(raw);
      return 'UNCLASSIFIED';
    }
    return code;
  };

  // 2. Prior-year actuals lookup keyed by hub + course (GUID then name) + month.
  const priorByGuid = new Map();
  const priorByName = new Map();
  (Array.isArray(priorSessions) ? priorSessions : []).forEach(s => {
    if (!includeBundleChildren && s?.isBundleChild) return;
    const date = parseLocalDate(s?.startDate);
    if (!date) return;
    const seats = Number(s?.seatsTaken) || 0;
    if (seats <= 0) return;
    const hub = hubOf(s.locationName, s.campus || s.campusName);
    const tail = `${hub}||${monthKeyOf(date)}`;
    if (s.courseGuid) {
      const k = `${s.courseGuid}||${tail}`;
      priorByGuid.set(k, (priorByGuid.get(k) || 0) + seats);
    }
    const nk = `${normalizeCourseName(s.courseName || s.name)}||${tail}`;
    priorByName.set(nk, (priorByName.get(nk) || 0) + seats);
  });

  const priorActualFor = (row, draftMonthKey) => {
    const [y, m] = draftMonthKey.split('-').map(Number);
    const priorMonthKey = `${y - priorYearOffset}-${String(m).padStart(2, '0')}`;
    const tail = `${row.hub}||${priorMonthKey}`;
    if (row.courseGuid) {
      const gk = `${row.courseGuid}||${tail}`;
      if (priorByGuid.has(gk)) return priorByGuid.get(gk);
    }
    return priorByName.get(`${normalizeCourseName(row.course)}||${tail}`) || 0;
  };

  // 3. Aggregate planned registrations per hub + course + month.
  const rowsMap = new Map(); // `hub||course` -> row
  earning.forEach(s => {
    const courseName = (s.name || s.courseName || 'Untitled').trim() || 'Untitled';
    const hub = hubOf(s.locationName, s.campus || s.campusName);
    const seats = projectedSeats(s, seatMode);
    const monthKey = monthKeyOf(parseLocalDate(s.startDate));
    const rowKey = `${hub}||${courseName}`;
    if (!rowsMap.has(rowKey)) {
      rowsMap.set(rowKey, {
        hub,
        course: courseName,
        courseGuid: s.courseGuid || null,
        planned: {},
        prior: {},
        plannedTotal: 0,
        priorTotal: 0
      });
    }
    const row = rowsMap.get(rowKey);
    if (!row.courseGuid && s.courseGuid) row.courseGuid = s.courseGuid;
    row.planned[monthKey] = (row.planned[monthKey] || 0) + seats;
    row.plannedTotal += seats;
  });

  // 4. Fill prior-year actuals for every row across the month timeline.
  rowsMap.forEach(row => {
    months.forEach(mo => {
      const prior = priorActualFor(row, mo.key);
      if (prior > 0) { row.prior[mo.key] = prior; row.priorTotal += prior; }
    });
  });

  // 5. Sort by hub order (WA, HI, CA, VA, then Unclassified) then course.
  const hubRank = h => { const i = HUB_CODES.indexOf(h); return i === -1 ? HUB_CODES.length : i; };
  const rows = [...rowsMap.values()].sort(
    (a, b) => hubRank(a.hub) - hubRank(b.hub) || a.course.localeCompare(b.course)
  );

  return {
    months,
    rows,
    priorYearOffset,
    unclassified: [...unclassified].sort((a, b) => a.localeCompare(b)),
    seatMode
  };
}

/**
 * Shape the hub sanity-check report into an .xlsx array-of-arrays with month
 * header groups, per-hub subtotals, a grand total, and a Variance (Plan − PY)
 * column per month.
 *
 * @param {object} report result of buildHubSanityReport
 * @returns {{ aoa:any[][], merges:object[], colWidths:object[], varianceCols:number[], autofilterRef:object }}
 */
export function hubSanityToWorkbookData(report) {
  const { months, rows } = report;
  const COLS_PER_MONTH = 3; // Planned, Prior Actual, Variance
  const firstCol = 2;       // 0 Hub, 1 Course
  const monthStart = i => firstCol + i * COLS_PER_MONTH;
  const totalStart = monthStart(months.length);
  const width = totalStart + COLS_PER_MONTH;
  const pyLabel = `PY-${report.priorYearOffset || 1} Actual`;

  const blankRow = () => new Array(width).fill('');
  const aoa = [];
  const merges = [];
  const varianceCols = [];

  // Row 0: month headers (merged over 3) + Totals group.
  const monthRow = blankRow();
  months.forEach((mo, i) => {
    const ms = monthStart(i);
    monthRow[ms] = mo.label;
    merges.push({ s: { r: 0, c: ms }, e: { r: 0, c: ms + COLS_PER_MONTH - 1 } });
  });
  monthRow[totalStart] = 'Totals';
  merges.push({ s: { r: 0, c: totalStart }, e: { r: 0, c: totalStart + COLS_PER_MONTH - 1 } });
  aoa.push(monthRow);

  // Row 1: Hub/Course + repeated metric sub-headers.
  const head = blankRow();
  head[0] = 'Hub';
  head[1] = 'Course';
  months.forEach((mo, i) => {
    const ms = monthStart(i);
    head[ms] = 'Planned';
    head[ms + 1] = pyLabel;
    head[ms + 2] = 'Variance';
    varianceCols.push(ms + 2);
  });
  head[totalStart] = 'Total Planned';
  head[totalStart + 1] = `Total ${pyLabel}`;
  head[totalStart + 2] = 'Total Variance';
  varianceCols.push(totalStart + 2);
  aoa.push(head);

  const hubDisplay = h => (h === 'UNCLASSIFIED' ? 'Unclassified' : `${h} ${hubName(h)}`.trim());

  const writeCompareRow = (row, label, agg, excelRow) => {
    row[0] = label;
    months.forEach((mo, i) => {
      const ms = monthStart(i);
      const plan = agg.planned[mo.key] || 0;
      const prior = agg.prior[mo.key] || 0;
      // Keep numeric zeros and formulas in every month so users can enter a
      // value into any previously-empty cell and Excel recalculates immediately.
      row[ms] = plan;
      row[ms + 1] = prior;
      row[ms + 2] = formulaCell(`${columnName(ms)}${excelRow}-${columnName(ms + 1)}${excelRow}`, plan - prior);
    });
    const plannedCells = months.map((mo, i) => `${columnName(monthStart(i))}${excelRow}`).join(',');
    const priorCells = months.map((mo, i) => `${columnName(monthStart(i) + 1)}${excelRow}`).join(',');
    row[totalStart] = formulaCell(`SUM(${plannedCells})`, agg.plannedTotal);
    row[totalStart + 1] = formulaCell(`SUM(${priorCells})`, agg.priorTotal);
    row[totalStart + 2] = formulaCell(`${columnName(totalStart)}${excelRow}-${columnName(totalStart + 1)}${excelRow}`, agg.plannedTotal - agg.priorTotal);
  };

  const writeSumRow = (row, label, agg, sourceExcelRows) => {
    const excelRow = aoa.length + 1;
    row[0] = label;
    const sumSources = col => `SUM(${sourceExcelRows.map(r => `${columnName(col)}${r}`).join(',')})`;
    months.forEach((mo, i) => {
      const ms = monthStart(i);
      const plan = agg.planned[mo.key] || 0;
      const prior = agg.prior[mo.key] || 0;
      row[ms] = formulaCell(sumSources(ms), plan);
      row[ms + 1] = formulaCell(sumSources(ms + 1), prior);
      row[ms + 2] = formulaCell(`${columnName(ms)}${excelRow}-${columnName(ms + 1)}${excelRow}`, plan - prior);
    });
    row[totalStart] = formulaCell(sumSources(totalStart), agg.plannedTotal);
    row[totalStart + 1] = formulaCell(sumSources(totalStart + 1), agg.priorTotal);
    row[totalStart + 2] = formulaCell(`${columnName(totalStart)}${excelRow}-${columnName(totalStart + 1)}${excelRow}`, agg.plannedTotal - agg.priorTotal);
  };

  const emptyAgg = () => ({ planned: {}, prior: {}, plannedTotal: 0, priorTotal: 0 });
  const accumulate = (agg, r) => {
    months.forEach(mo => {
      if (r.planned[mo.key]) agg.planned[mo.key] = (agg.planned[mo.key] || 0) + r.planned[mo.key];
      if (r.prior[mo.key]) agg.prior[mo.key] = (agg.prior[mo.key] || 0) + r.prior[mo.key];
    });
    agg.plannedTotal += r.plannedTotal;
    agg.priorTotal += r.priorTotal;
  };

  // Body: rows grouped by hub, a subtotal after each hub, then a grand total.
  const grand = emptyAgg();
  let currentHub = null;
  let hubAgg = null;
  let hubDataExcelRows = [];
  const subtotalExcelRows = [];
  rows.forEach(r => {
    if (currentHub !== null && r.hub !== currentHub) {
      const sub = blankRow();
      writeSumRow(sub, `${hubDisplay(currentHub)} — Subtotal`, hubAgg, hubDataExcelRows);
      aoa.push(sub);
      subtotalExcelRows.push(aoa.length);
    }
    if (r.hub !== currentHub) { currentHub = r.hub; hubAgg = emptyAgg(); hubDataExcelRows = []; }

    const dataRow = blankRow();
    writeCompareRow(dataRow, hubDisplay(r.hub), r, aoa.length + 1); // sets col0 (Hub) + month/total cols
    dataRow[1] = r.course;
    aoa.push(dataRow);
    hubDataExcelRows.push(aoa.length);

    accumulate(hubAgg, r);
    accumulate(grand, r);
  });
  if (currentHub !== null) {
    const sub = blankRow();
    writeSumRow(sub, `${hubDisplay(currentHub)} — Subtotal`, hubAgg, hubDataExcelRows);
    aoa.push(sub);
    subtotalExcelRows.push(aoa.length);
  }
  const grandRow = blankRow();
  if (subtotalExcelRows.length) writeSumRow(grandRow, 'GRAND TOTAL', grand, subtotalExcelRows);
  else grandRow[0] = 'GRAND TOTAL';
  aoa.push(grandRow);

  const colWidths = new Array(width).fill(null).map((_, i) => {
    if (i === 0) return { wch: 18 }; // Hub
    if (i === 1) return { wch: 34 }; // Course
    if (i >= totalStart) return { wch: 15 };
    return { wch: 11 };
  });

  const autofilterRef = { s: { r: 1, c: 0 }, e: { r: 1 + rows.length, c: width - 1 } };
  return { aoa, merges, colWidths, varianceCols, autofilterRef };
}
